// File Upload Area - drag and drop file upload component

import { Label } from "@repo/ui";
import { Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface FileUploadAreaProps {
  acceptedFileTypes: string;
  onFilesSelected: (files: FileList) => void;
  /** Ignores picks and drops, e.g. while the selected files are being submitted. */
  disabled?: boolean;
}

export function FileUploadArea({
  acceptedFileTypes,
  onFilesSelected,
  disabled = false,
}: FileUploadAreaProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFilesSelected],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragOver(false);
    },
    [],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      if (!disabled && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [disabled, onFilesSelected],
  );

  return (
    <div>
      <Label htmlFor="uploadInput">Upload Content</Label>
      {/* Use a <label> element so the native browser activates the input —
          programmatic .click() is blocked on display:none inputs in iOS Safari
          and some Android Chrome contexts. The input is outside the label to
          prevent double-trigger (label click + input click). */}
      <label
        htmlFor="uploadInput"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-disabled={disabled || undefined}
        className={`mt-2 flex flex-col border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          disabled
            ? "cursor-not-allowed opacity-50"
            : isDragOver
              ? "cursor-pointer border-primary bg-primary/5"
              : "cursor-pointer hover:border-primary/50"
        }`}
      >
        <div>
          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {acceptedFileTypes}
          </p>
        </div>
      </label>
      <input
        id="uploadInput"
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        multiple
        disabled={disabled}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
